import { MatDialog } from '@angular/material';
import { Injectable } from '@angular/core';
import { concatMap, map, tap } from 'rxjs/operators';
import { Subject, Observable, throwError, of, from } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { switchMap, catchError } from 'rxjs/operators';

import { LooseObject } from '../../objects/loose-object';
import { PresignedUrl } from '../../objects/presigned-url';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../notification.service';
import { ClientStoreService } from '../../services/client-store.service';
import { FormattedErrorObject, BasicValidationErrorObject } from '../../objects/request-errors';
import { saveAs } from 'file-saver';
import { defaults, pick, get } from 'lodash-es';
import { PreviewComponent } from '../../shared/components/widget/file/preview/preview.component';
import imageCompression from 'browser-image-compression';
import { data_get, fallback } from '../../shared/utils/common';

const kFiletypeRegex = /\.([0-9a-z]+)(?:[\?#]|$)/i;

@Injectable({
  providedIn: 'root'
})
export class FileService {

  public objFile: File;
  public refreshFilesList$: Observable<boolean>;
  public refreshFilesListSource: Subject<boolean>;

  /**
   * list of accetable file types
   */
  private objAcceptableFileTypes = {
    'text/plain': ['txt'],
    'text/csv': ['csv'],
    'image/png': ['png'],
    'image/jpeg': ['jpg', 'jpeg'],
    'image/bmp': ['bmp'],
    'image/gif': ['gif'],
    'image/webp': ['webp'],
    'image/svg+xml': ['svg'],
    'image/heif': ['heif', 'heic'],
    'image/avif': ['avif'],
    'application/pdf': ['pdf'],
    'application/msword': ['doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
    'application/vnd.ms-powerpoint': ['ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['pptx'],
    'application/vnd.ms-excel': ['xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
    'application/zip': ['zip'],
    'application/x-zip-compressed': ['zip'], // this one is depricated but some windows still has this type when uploading zip https://mimetype.io/application/x-zip-compressed
    'application/vnd.rar': ['rar'],
    'application/vnd.oasis.opendocument.text': ['odt'],
    'application/vnd.oasis.opendocument.spreadsheet': ['ods'],
    'application/vnd.oasis.opendocument.presentation': ['odp'],
    'video/mp4': ['mp4'],
    'application/vnd.ms-outlook': ['msg'], //FC-5105 Added .msg file type for Outlook
    'application/x-msg': ['msg'],
  };

  constructor(
    private dialog: MatDialog,
    protected http: HttpClient,
    protected notificationService: NotificationService,
    protected clientStoreService: ClientStoreService,
  ) {
    this.refreshFilesListSource = new Subject<boolean>();
    this.refreshFilesList$ = this.refreshFilesListSource.asObservable();
  }

  /**
   * Uploads the file to the given bucket and returns its pre-signed URL along with a filename
   *
   * @param {File} objFile
   * @param {string} strBucket
   * @param {string|undefined} strFilename
   *
   * @returns {Observable<PresignedUrl>}
   */
  upload(objFile: File, strBucket: string = environment.temp_bucket, strFilename?: string, options: {
    hasErrorHandler?: boolean,
  } = {}): Observable<PresignedUrl> {
    options = Object.assign({
      hasErrorHandler: false,
    }, options);

    if (objFile.type && objFile.type.includes('image')) {

      // Skip compression for HEIC/HEIF as canvas-based compression is not supported
      const bIsHeicOrHeif = /image\/(heic|heif)/i.test(objFile.type);
      if (bIsHeicOrHeif) {
        this.objFile = objFile;
        return this.uploadSignedUrl(objFile, strBucket, strFilename, options);
      }

      let objCurrentClient = this.clientStoreService.getActiveClient();
      let intImageQuality: number = objCurrentClient.config.image_quality || 0.5;

      const objOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        initialQuality: intImageQuality,
        useWebWorker: true
      }

      let imageCompressed = from(imageCompression(objFile, objOptions));

      return imageCompressed.pipe(
        switchMap(objFileCompressed => {
          this.objFile = objFileCompressed;

          return this.uploadSignedUrl(objFileCompressed, strBucket, strFilename, options);
        })
      );
    } else {
      this.objFile = objFile;
      return this.uploadSignedUrl(objFile, strBucket, strFilename);
    }
  }

  /**
   * Generate a pre-signed URL along with a file name
   * @param objFile
   * @param strBucket
   * @param strFilename
   * @returns
   */
  uploadSignedUrl(objFile: File, strBucket: string, strFilename?: string, options: {
    hasErrorHandler?: boolean,
  } = {}): Observable<PresignedUrl> {
    options = Object.assign({
      hasErrorHandler: false,
    }, options);

    return this.getUploadSignedUrl(objFile, strBucket, strFilename).pipe(
      concatMap(objSignature => {
        let objHeaders = { headers: { 'Content-Type': this._getContentTypeForFile(objFile) } };

        return this.http.put(objSignature.url, objFile, objHeaders).pipe(
          concatMap(response => of(objSignature)),
          catchError(err => throwError(err))
        );
      }),
      catchError((error) => {
        if (options.hasErrorHandler) {
          return throwError(error);
        }

        this.presentArrayAsErrorMessages(
          'tmp_file_upload_error',
          this.getMessagesFromErrorResponse(error)
        );

        return throwError(
          'An error has occurred while uploading the temporary file. Please try again'
        );
      }),
    );
  }

  /**
   * Generates an upload pre-signed url for the given file
   *
   * @param {File} file
   * @param {string} bucket
   * @param {string|undefined} filename
   *
   * @returns {Observable<PresignedUrl>}
   */
  getUploadSignedUrl(file: File, bucket: string, filename?: string): Observable<PresignedUrl> {
    let requestData = this.urlSearchParams({
      bucket: bucket,
      content_type: `${file.type}`,
      filename,
    });
    const arExtension = file.name.match(kFiletypeRegex);
    const strExtension = (arExtension && arExtension[1]) ? arExtension[1].toLowerCase() : '';

    if (strExtension === 'msg') {
      const buildRequestData = (contentType?: string) => {
        const dataObj: LooseObject = {
          bucket: bucket,
          filename,
        };

        if (contentType) {
          dataObj['content_type'] = contentType;
        }

        return this.urlSearchParams(dataObj);
      };
      const msg_content_types = [
        // server-accepted content types for Outlook .msg files
        'application/vnd.ms-outlook',
        'application/x-msg',
      ];

      const attempt = (index: number, lastError?: any): Observable<PresignedUrl> => {
        if (index < 0 || index >= msg_content_types.length) {
          return throwError(lastError || new Error('No valid content_type accepted by server for .msg'));
        }

        const params = buildRequestData(msg_content_types[index]);

        return this.http.post<PresignedUrl>(this.url('get_upload_url'), params.toString()).pipe(
          catchError(err => {
            if (err && err.status === 422) {
              return attempt(index + 1, err);
            }
            return throwError(err);
          })
        );
      };

      const browserType = this._getContentTypeForFile(file);
      if (browserType) {
        const params = buildRequestData(browserType);
        return this.http.post<PresignedUrl>(this.url('get_upload_url'), params.toString()).pipe(
          catchError(err => {
            if (err && err.status === 422) {
              // fall back to msg_content_types list
              return attempt(0, err);
            }
            return throwError(err);
          })
        );
      }

      return attempt(0);
    }

    return this.http.post<PresignedUrl>(
      this.url('get_upload_url'), requestData.toString()
    );
  }

  /**
   * Generates a pre-signed url which contains the viewable object
   *
   * @param {string} fileName
   * @param {string} contentType
   * @param {string} bucket
   * @param {number} expiry - defaults to 2hrs / 120 minutes
   *
   * @returns {Observable<PresignedUrl>}
   */
  getObjectSignedUrl(fileName: string, bucket: string = environment.temp_bucket, expiry: number = 120): Observable<PresignedUrl> {
    let requestData = this.urlSearchParams({
      object_key: fileName,
      bucket: bucket,
      expiry: expiry
    });

    return this.http.post<PresignedUrl>(
      this.url('get_object_url'), requestData.toString()
    );
  }

  /**
   * Calls the api for getting all the versions of the current object
   *
   * @param {string} fileName
   */
  getObjectHistory(fileName: string) {
    let requestData = this.urlSearchParams({
      object_key: fileName
    });

    return this.http.post(
      this.url('get_object_versions'), requestData.toString()
    );
  }


  /**
   * Gets the object url for downloading
   *
   * @param uploadName
   * @param contentType
   * @param fileName
   */
  download(uploadName: string, contentType: string, fileName: string) {
    let requestData = this.urlSearchParams({
      object_key: uploadName,
      content_type: contentType,
      file_name: fileName
    });

    return this.http.post(
      this.url('download'), requestData.toString()
    );
  }

  /**
   * Upload images file to s3
   *
   *
   * @param file
   * @param file_type
   * @param file_size
   * @returns {Observable<PresignedUrl>}
   */
  uploadImage(file: File, file_type: string, file_size: number): Observable<PresignedUrl> {
    // if file size is less than 30mb
    if (file_size / 1024 / 1024 < 30) {
      if (file_type == 'image/png' || file_type == 'image/jpeg') {
        return this.upload(file);
      } else {
        return throwError(this.notificationService.sendNotification("upload_failed", "photos_invalid", "warning"));
      }
    } else {
      return throwError(this.notificationService.sendNotification('not_allowed', 'invalid_file_size', 'warning'));
    }
  }

  /**
   * Copies the file to client bucket and saves it to activities record
   *
   * @param data
   */
  save(data) {
    let requestData = this.urlSearchParams(data);

    return this.http.post(
      this.url('save'), requestData.toString()
    );
  }

  /**
   * Creates a new url search params object, uses the given loose object as its
   * `data` attribute then returns it.
   *
   * @param {LooseObject} data
   *
   * @returns {URLSearchParams}
   */
  protected urlSearchParams(data: LooseObject): URLSearchParams {
    let body = new URLSearchParams();
    body.append('data', JSON.stringify(data));

    return body;
  }

  /**
   * Returns a url to the records api and appends the given path.
   *
   * @param {String} path
   *
   * @returns {String}
   */
  protected url(path: string): string {
    return `${environment.url}/file/${path}`;
  }

  /**
   * Extracts the error messages from the HttpErrorResponse object.
   *
   * @param {string|FormattedErrorObject|BasicValidationErrorObject|Error} error
   *
   * @returns {string[]}
   */
  protected getMessagesFromErrorResponse(response: string | FormattedErrorObject | BasicValidationErrorObject | Error): string[] {
    if (typeof response === 'string') {
      return [response];

    } else if (response instanceof Error) {
      return [response.message];

    } else if (response.errors === undefined) {
      let errors = <BasicValidationErrorObject>response;

      return Object.values(errors).flat(2);

    } else {
      let errors = <FormattedErrorObject>response;

      return errors.errors.map(_error => _error.detail);
    }
  }

  /**
   * Opens up a notification box containing the supplied header and error messages.
   *
   * @param header
   * @param messages
   *
   * @returns {void}
   */
  protected presentArrayAsErrorMessages(header: string, messages: string[]): void {
    let error_messages = messages.join('\n');

    this.notificationService.sendNotification(header, error_messages, 'danger');
  }

  /**
   * Transform a url into blob
   *
   * @param {string} url
   *
   * @returns {Observable<Blobl>}
   */
  toBlob$(url: string): Observable<Blob> {
    return from(fetch(url))
      .pipe(
        switchMap((response: Response) => from(response.blob()))
      );
  }

  /**
   * Downloads the content from given url into a file instance
   *
   * @param {string} url
   * @param {FileOptions} options
   *
   * @returns {Observable<File>}
   */
  webToFile$(url: string, options: FileOptions): Observable<File> {
    return this.toBlob$(url)
      .pipe(
        map((blob) => new File([blob], options.name, {
          type: options.type
        })),
      );
  }

  /**
   * Download the file and save as a file to the user
   *
   * @param {string} url
   * @param {string} filename
   *
   * @returns {Observable<boolean>}
   */
  downloadFileFromUrl$(url: string, filename: string): Observable<boolean> {
    return this.toBlob$(url).pipe(
      tap((blob) => {
        saveAs(blob, filename);
      }),
      map(() => true)
    );
  }

  /**
   * Downloads the given web content and upload it to the specified bucket (tmp by default)
   * and make it as an attachment to certain process
   *
   * @param {string} url
   * @param {WebAsAnAttachmentOptions} options
   *
   * @returns {Observable<WebAttachment>}
   */
  webAsAnAttachment$(url: string, options: WebAsAnAttachmentOptions): Observable<WebAttachment> {
    options = defaults(options, {
      generatesUniqueFilenameUponUpload: false,
    });

    return this.webToFile$(url, {
      name: options.filename,
      type: options.filetype
    })
      .pipe(
        switchMap((file) => this.upload(
          file, options.bucket, options.generatesUniqueFilenameUponUpload ? undefined : options.filename
        ).pipe(
          map((presigned) => ({
            ...presigned,
            ...pick(file, ['size', 'type'])
          }))
        ))
      );
  }

  /**
   * validate file type and file extension, it should exist on file acceptable file type list
   *
   * @param objFile
   * @returns
   */
  hasValidUploadFile(objFile: File): boolean {
    const fileType = objFile.type || this._getContentTypeForFile(objFile);
    let arFileExtension = this.objAcceptableFileTypes[fileType] || [];

    if (arFileExtension) {

      let arExtension = objFile.name.match(kFiletypeRegex);
      return arFileExtension.includes(arExtension[1]);
    }

    return false;
  }

  /**
   * Determine content-type for a file. Prefer file.type, otherwise derive from
   * the extension using our acceptable types mapping.
   */
  private _getContentTypeForFile(file: File): string {
    if (file.type) {
      return file.type;
    }

    const arExtension = file.name.match(kFiletypeRegex);
    const strExtension = (arExtension && arExtension[1]) ? arExtension[1].toLowerCase() : '';

    if (strExtension) {
      for (const [mime, exts] of Object.entries(this.objAcceptableFileTypes)) {
        if (exts.includes(strExtension)) {
          return mime;
        }
      }
    }

    throw new Error('Unable to determine content type for the file.');
  }

  /**
   * Sanitizes the filename, especially the file type
   *
   * @param {File} objFile
   *
   * @returns {File}
   */
  sanitizeFilename(objFile: File): File {
    const strFilenameWithoutExt: string = this._getFileNameWithoutExtension(objFile.name);
    const strFileExtentionLowercase: string = this._getFileExtensionInLowercase(objFile.name);
    const strSanitizedFilename: string = `${strFilenameWithoutExt}.${strFileExtentionLowercase}`;

    return new File(
      [objFile],
      strSanitizedFilename,
      { type: objFile.type },
    );
  }

  /**
   * Previews the given file in a modal
   *
   * @param {string} strUploadName
   * @param {string} strFileName
   *
   * @returns {Observable<void>}
   */
  previewFile(strUploadName: string, strFileName: string): Observable<void> {
    return this.getObjectSignedUrl(strUploadName, environment.client_bucket).pipe(
      switchMap((objResult: PresignedUrl) => {
        return this.dialog.open(PreviewComponent, {
          width: "90%",
          height: "auto",
          data: {
            path: objResult.url,
            file_name: strFileName
          },
          disableClose: true
        }).afterOpened();
      })
    );
  }

  private _getFileNameWithoutExtension(strFilename: string): string {
    const numLastDotIndex: number = strFilename.lastIndexOf('.');

    if (numLastDotIndex === -1) {
        return strFilename;
    }
    return strFilename.substring(0, numLastDotIndex);
  }

  private _getFileExtensionInLowercase(strFilename: string): string {
    const arFilenameData: RegExpMatchArray = strFilename.match(kFiletypeRegex);
    const strExtension: string = get(arFilenameData, '1', '') || '';

    return strExtension.toLowerCase();
  }

  findDetails(extractedContent) {
    let body = new URLSearchParams();
    let extracted = fallback(data_get(extractedContent, 'text'), {
      fallback: () => {}
    });

    body.append('data', JSON.stringify(extracted));

    return this.http.post(this.url('find_details'), body.toString()).pipe(
      catchError((error) => {
        let errorMessage = error.error;

        console.log(error);
        this.notificationService.notifyError(errorMessage);
        return throwError(errorMessage);
      }),
      map(response => response),
    );
  }

  extractPdf(details) {
    let headers: HttpHeaders = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http.post(environment['extract_pdf'], details, {
      headers,
      observe: 'response'
    }).pipe(
      map(response => ({ success: true, data: response })),
      catchError((error) => {
        let errorMessage = error.error;

        return of({ success: false, errorMessage, originalError: error });
      }),
    );
  }
}

interface FileOptions {
  name: string;
  type: string;
}

interface WebAsAnAttachmentOptions {
  filename: string;
  filetype: string;
  bucket?: string;
  generatesUniqueFilenameUponUpload?: boolean;
}

interface WebAttachment extends PresignedUrl {
  size: number;
  type: string;
}